import { combineSlices, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';

import { persistUi, uiSlice } from '@features/shell';
import { baseApi } from '@shared/api/baseApi';
import { sessionSlice } from '@shared/session/sessionSlice';
import { REGEX } from '@shared/constants/regex';

const rootReducer = combineSlices(uiSlice, sessionSlice, baseApi);

export type RootState = ReturnType<typeof rootReducer>;

export function makeStore(preloadedState?: Partial<RootState>) {
  const store = configureStore({
    reducer: rootReducer,
    preloadedState,
    middleware: (getDefault) =>
      getDefault({
        // Download mutations (exports, PDFs, attachments) hand back a Blob, which is
        // saved straight to disk; it is the one non-serializable value we allow. Setting
        // the list replaces RTK's defaults, so they are repeated here.
        serializableCheck: {
          ignoredActionPaths: ['meta.arg', 'meta.baseQueryMeta', 'payload.blob'],
          ignoredPaths: [REGEX.mutationBlobPath],
        },
      }).concat(baseApi.middleware),
  });
  setupListeners(store.dispatch);
  return store;
}

export type AppStore = ReturnType<typeof makeStore>;
export type AppDispatch = AppStore['dispatch'];

/** Persist per-browser UI choices whenever they change. */
export function watchUiPersistence(store: AppStore): () => void {
  let previous = store.getState().ui;
  return store.subscribe(() => {
    const current = store.getState().ui;
    if (current !== previous) {
      previous = current;
      persistUi(current);
    }
  });
}
